// Mixer — one Tone.Channel per layer (volume/mute/solo/pan + an FX send and a
// level meter) into a master bus → destination (docs/04 §4.8). The master bus
// also feeds the export recorder (Phase 06). Live play routes to the master.

import * as Tone from "tone";
import { FX_BUS } from "./fx.ts";

interface ChannelStrip {
  channel: Tone.Channel;
  meter: Tone.Meter;
}

export class Mixer {
  private readonly master: Tone.Channel;
  private readonly masterMeter: Tone.Meter;
  private readonly strips = new Map<string, ChannelStrip>();

  constructor() {
    this.master = new Tone.Channel();
    this.masterMeter = new Tone.Meter();
    this.master.connect(this.masterMeter);
    this.master.toDestination();
  }

  get masterNode(): Tone.ToneAudioNode {
    return this.master;
  }

  addChannel(id: string): Tone.Channel {
    const channel = new Tone.Channel();
    channel.connect(this.master);
    channel.send(FX_BUS, -Infinity); // no FX send until the user dials one in
    const meter = new Tone.Meter();
    channel.connect(meter);
    this.strips.set(id, { channel, meter });
    return channel;
  }

  removeChannel(id: string): void {
    const strip = this.strips.get(id);
    if (strip) {
      strip.channel.dispose();
      strip.meter.dispose();
      this.strips.delete(id);
    }
  }

  setVolume(id: string, db: number): void {
    const s = this.strips.get(id);
    if (s) s.channel.volume.value = db;
  }
  setMute(id: string, muted: boolean): void {
    const s = this.strips.get(id);
    if (s) s.channel.mute = muted;
  }
  setSolo(id: string, solo: boolean): void {
    const s = this.strips.get(id);
    if (s) s.channel.solo = solo;
  }
  setPan(id: string, pan: number): void {
    const s = this.strips.get(id);
    if (s) s.channel.pan.value = pan;
  }
  /** FX send amount 0..1 (→ dB, with 0 = off). */
  setSend(id: string, amount: number): void {
    const s = this.strips.get(id);
    if (s) s.channel.send(FX_BUS, amount <= 0 ? -Infinity : Tone.gainToDb(amount));
  }

  /** Normalized 0..1 level for a layer (from its meter). */
  getLevel(id: string): number {
    const s = this.strips.get(id);
    if (!s) return 0;
    return dbToLevel(asNumber(s.meter.getValue()));
  }

  getMasterLevel(): number {
    return dbToLevel(asNumber(this.masterMeter.getValue()));
  }

  setMasterVolume(db: number): void {
    this.master.volume.value = db;
  }

  rampMasterVolume(db: number, sec: number): void {
    this.master.volume.rampTo(db, sec);
  }

  dispose(): void {
    for (const s of this.strips.values()) {
      s.channel.dispose();
      s.meter.dispose();
    }
    this.strips.clear();
    this.masterMeter.dispose();
    this.master.dispose();
  }
}

function asNumber(v: number | number[]): number {
  return Array.isArray(v) ? (v[0] ?? -Infinity) : v;
}

// Map meter dB (~-60..0) to a 0..1 level for visuals.
function dbToLevel(db: number): number {
  if (!Number.isFinite(db)) return 0;
  return Math.max(0, Math.min(1, (db + 60) / 60));
}
